import React, { useMemo, useRef, useState, useEffect } from 'react';
import { TableList, Page, Button, PageEmptyState, PageErrorState, FilterPickerGroup, Searchbox, WithStatusIcon, PageContentHeader, SingleSelectFilterPicker } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { selectors } from "../../store/state-selectors";
import { useIoContext, useStore, useStoreActions, useStoreStatic } from "../../contexts";
import { CreateProposalButton } from "../../components/CreateProposalButton";
import PageLoadingState from "../../components/PageLoadingState";
import PayoutsEmptyStateIcon from "./icons/payouts-empty-state.svg";
import PayoutsDashboardRow from "./PayoutsDashboardRow/PayoutsDashboardRow";
import { QboStatusFilter, applyFilters, applySort, getQboStatusFilterOptions } from "./PayoutsDashboard.utils";
import SeeWhatItsLike from "../../components/SeeWhatItsLike/SeeWhatItsLike";
import img1 from "./SeeWhatItsLikeImages/1.png";
import img2 from "./SeeWhatItsLikeImages/2.png";
import compact from 'lodash/compact';
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
const title = 'Payouts';
function getTableConfig(_ref) {
  let {
    showQboIntegration,
    isPendingQboIntegrationSetup
  } = _ref;
  const syncStatusText = 'Sync Status';
  const data = compact([{
    columnDef: {
      width: 108,
      sortable: true
    },
    headerItem: {
      id: 'invoiceNo',
      value: 'Deposit date'
    }
  }, {
    columnDef: {
      width: 100,
      sortable: true,
      hAlign: 'end'
    },
    headerItem: {
      id: 'companyName',
      value: 'Amount'
    }
  }, {
    columnDef: {
      width: [90, 100],
      sortable: true
    },
    headerItem: {
      id: 'issuedOn',
      value: 'Status'
    }
  }, showQboIntegration && {
    columnDef: {
      width: [100, 120],
      sortable: false
    },
    headerItem: {
      id: 'syncStatus',
      value: isPendingQboIntegrationSetup ? /*#__PURE__*/React.createElement(WithStatusIcon, {
        "data-testid": "missing-account-mapping-error",
        info: "Please finish setting up QBO by selecting the accounts in the integration setup page"
      }, syncStatusText) : syncStatusText
    }
  }]);
  return {
    headerItems: data.map(d => d.headerItem),
    columnDefs: data.map(d => d.columnDef)
  };
}
export function PayoutsDashboard(props) {
  const {
    data,
    isRefreshing
  } = props;
  const {
    logBI
  } = useStoreActions();
  const [sort, setSort] = useState({
    colIndex: 0,
    sort: 'desc'
  });
  const scrollElRef = useRef(null);
  const {
    openNewTab
  } = useIoContext();
  const {
    getState
  } = useStoreStatic();
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11401);
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const isPendingQboIntegrationSetup = useStore(selectors.integration.isPendingQboIntegrationSetup);
  const {
    urlState: {
      search,
      qbo: qboStatusFilter
    },
    changeUrlState
  } = useStateFromUrl({
    search: {
      defaultValue: ''
    },
    qbo: {
      defaultValue: QboStatusFilter.All
    }
  });
  const qboStatusFilterOptions = useMemo(() => {
    return getQboStatusFilterOptions(applyFilters({
      payouts: data.payouts,
      qboStatusFilter: null,
      search
    }));
  }, [data, search]);
  const sortedAndFiltered = useMemo(() => {
    const filtered = applyFilters({
      payouts: data.payouts,
      qboStatusFilter,
      search
    });
    return applySort(filtered, sort);
  }, [data, sort, search, qboStatusFilter]);
  const {
    paginatedData,
    onScroll
  } = useInfiniteScroll({
    data: sortedAndFiltered,
    scrollElRef
  });
  const {
    headerItems,
    columnDefs
  } = useMemo(() => getTableConfig({
    showQboIntegration,
    isPendingQboIntegrationSetup
  }), [showQboIntegration, isPendingQboIntegrationSetup]);
  function onSortChange(sort) {
    logBI(11400, {
      column: sort ? headerItems[sort.colIndex].value : 'none'
    });
    setSort(sort);
  }
  function onClearSearch() {
    changeUrlState({
      search: ''
    });
    debouncedLogSearchToBI.onClear();
  }
  function exportToCsv() {
    const mgmtHost = selectors.mgmtHost(getState());
    openNewTab("".concat(mgmtHost, "/business/export-payouts"));
  }
  return data.payouts.length ? /*#__PURE__*/React.createElement(Page, {
    "data-testid": "payments-dashboard",
    ref: scrollElRef,
    header: title,
    actions: /*#__PURE__*/React.createElement(Button, {
      "data-testid": "payments-export-csv",
      onClick: exportToCsv
    }, "Export to CSV"),
    subheader: "Review your paid invoices",
    loading: isRefreshing,
    onScroll: onScroll
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs,
    expandableRows: true,
    singleExpandOnly: true,
    sort: sort,
    onSortChange: onSortChange
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    style: {
      marginBlockEnd: '24px'
    }
  }, /*#__PURE__*/React.createElement(Searchbox, {
    registerHotkey: true,
    value: search,
    "data-testid": "payments-dashboard-search",
    onChange: event => {
      const searchStr = event.target.value;
      changeUrlState({
        search: searchStr
      });
      debouncedLogSearchToBI.onChange(searchStr);
    },
    onClear: onClearSearch
  }), showQboIntegration && qboStatusFilterOptions.length > 0 && /*#__PURE__*/React.createElement(SingleSelectFilterPicker, {
    "data-testid": "payouts-qbo-filter",
    label: "Sync status",
    value: qboStatusFilter,
    options: qboStatusFilterOptions,
    onChange: option => option && changeUrlState({
      qbo: option.value
    })
  })), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems,
    "data-testid": "payments-dashboard-table-header"
  })), sortedAndFiltered.length ? paginatedData.map((payout, index) => /*#__PURE__*/React.createElement(PayoutsDashboardRow, {
    key: payout.id,
    payout: payout,
    index: index,
    invoiceHeaders: data.invoiceHeaders
  })) : /*#__PURE__*/React.createElement(PageEmptyState, {
    illustration: /*#__PURE__*/React.createElement(PayoutsEmptyStateIcon, null),
    title: "No payouts match your request",
    message: "Try adjusting your search",
    actions: /*#__PURE__*/React.createElement(Button, {
      "data-testid": "payments-dashboard-empty-search-clear-btn",
      onClick: onClearSearch
    }, "Clear search")
  }))) : /*#__PURE__*/React.createElement(Page, {
    header: title,
    loading: isRefreshing
  }, /*#__PURE__*/React.createElement(PageEmptyState, {
    "data-testid": "payments-empty",
    illustration: /*#__PURE__*/React.createElement(PayoutsEmptyStateIcon, null),
    title: "Automatic payments, with confidence",
    message: "Clients won't get any surprises at the end of the month. No manual collection or payment is needed \u2014 everything is managed by Anchor, automatically.",
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SeeWhatItsLike, {
      steps: [{
        title: 'Cashflow - flows!',
        subtitle: 'During the proposal approval stage, your clients can choose how they’d like to pay.',
        image: {
          src: img1,
          width: 1198,
          height: 700
        }
      }, {
        title: 'Get paid on time',
        subtitle: 'Anchor will send reminders, handle payment processing, and ensure that late payments are a thing of the past!',
        image: {
          src: img2,
          width: 1246,
          height: 748
        }
      }],
      "data-testid": "payouts-dashboard"
    }), /*#__PURE__*/React.createElement(CreateProposalButton, {
      key: "create-proposal-button",
      hidePrefix: true,
      "data-testid": "payments-empty-new-btn"
    }))
  }));
}
export default function WrappedPayoutsDashboard() {
  const payouts = useStore(state => state.payouts.data);
  const {
    getState
  } = useStoreStatic();
  const {
    fetchPayouts,
    fetchIntegrations
  } = useStoreActions();
  useEffect(() => {
    document.title = title;
  }, []);
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: () => {
      const showQboIntegration = selectors.integration.showQboIntegration(getState());
      if (showQboIntegration) {
        fetchIntegrations();
      }
      return fetchPayouts();
    },
    initialData: payouts,
    renderChild: _ref2 => {
      let {
        data,
        loading
      } = _ref2;
      return /*#__PURE__*/React.createElement(PayoutsDashboard, {
        data: data,
        isRefreshing: loading
      });
    },
    renderLoader: () => /*#__PURE__*/React.createElement(PageLoadingState, {
      header: title,
      loading: true
    }),
    renderError: _ref3 => {
      let {
        refresh,
        error
      } = _ref3;
      return /*#__PURE__*/React.createElement(PageErrorState, {
        title: title,
        error: error,
        onRefresh: () => refresh()
      });
    }
  });
}